import React, { useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { X, CalendarDays, Clock, Eye, Loader, Send, AlertCircle, CheckCircle } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import { useModalLayer } from '../../lib/modalStack';
import { sanitizeEmailHtml } from './email/emailSanitize';
import {
  scheduleBroadcast,
  type BroadcastRecipient,
  type AttachmentPayload,
} from './email/emailApi';

interface Props {
  /** Initial email data to schedule */
  emailData: {
    to: string;
    toName: string;
    subject: string;
    htmlBody: string;
    applicationId?: string;
    attachments?: { name: string; mimeType: string; base64Content?: string }[];
    /** Document templates to generate from the application at send time (Slice 3); forwarded to /api/email/schedule as ids. */
    documentTemplateIds?: string[];
    bcc?: string;
    senderAddress?: string;
    /** Optional idempotency key for the schedule request (per compose session). */
    idempotencyKey?: string;
  };
  /** Called when the modal is dismissed */
  onClose: () => void;
  /** Called after successful scheduling */
  onScheduled?: () => void;
  /** If provided, we are rescheduling an existing scheduled email */
  existingScheduleId?: string;
  existingSendAt?: string;
  existingTimezone?: string;
  /**
   * Batch/broadcast mode. When present the modal schedules a campaign via
   * POST /api/email/broadcast/schedule instead of the single-email endpoint.
   * `sendAt` is persisted as UTC ISO; `timezone` is display-only metadata.
   */
  broadcast?: {
    recipients: BroadcastRecipient[];
    batchId?: string;
    sentBy?: string;
    senderAddress?: string;
    idempotencyKey: string;
    attachments?: AttachmentPayload[];
    /**
     * Document templates to generate per recipient at execution time
     * (Slice 4, fork F-1). Stored per schedule entry; generated from
     * each recipient's own application when the campaign fires.
     */
    documentTemplateIds?: string[];
  };
}

export function wallClockToUtcISO(dateStr: string, timeStr: string, timeZone: string): string {
  if (!dateStr || !timeStr) return '';
  const targetWallClockStr = `${dateStr}T${timeStr}:00`;
  const utcGuess = new Date(`${targetWallClockStr}Z`);
  if (isNaN(utcGuess.getTime())) return new Date(`${dateStr}T${timeStr}`).toISOString();

  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone || 'UTC',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hourCycle: 'h23'
    });

    const parts = formatter.formatToParts(utcGuess);
    const map: Record<string, string> = {};
    for (const p of parts) {
      if (p.type !== 'literal') map[p.type] = p.value;
    }

    const formattedInTz = `${map.year}-${map.month}-${map.day}T${map.hour}:${map.minute}:${map.second}Z`;
    const tzAsUtcMs = new Date(formattedInTz).getTime();
    const utcMs = utcGuess.getTime();
    const offsetMs = tzAsUtcMs - utcMs;

    const actualUtcMs = utcMs - offsetMs;
    return new Date(actualUtcMs).toISOString();
  } catch (err) {
    return new Date(`${dateStr}T${timeStr}`).toISOString();
  }
}

function toLocalDatetimeString(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
}

export default function EmailScheduleModal({
  emailData, onClose, onScheduled, existingScheduleId,
  existingSendAt, existingTimezone, broadcast,
}: Props) {
  const zIndex = useModalLayer();
  const [sendDate, setSendDate] = useState(toLocalDatetimeString(existingSendAt).split('T')[0] || '');
  const [sendTime, setSendTime] = useState(toLocalDatetimeString(existingSendAt).split('T')[1] || '');
  const [timezone, setTimezone] = useState(existingTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // ES-6: a partial schedule (only some recipients' rows persisted)
  // is rendered as a WARNING — never as a full success.
  const [warning, setWarning] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const submittingRef = React.useRef(false);

  // Compute min date = today
  const todayStr = new Date().toISOString().split('T')[0];
  const now = new Date();
  const nowHours = now.getHours().toString().padStart(2, '0');
  const nowMins = now.getMinutes().toString().padStart(2, '0');

  // Compute min time = current time (only if date is today)
  const isToday = sendDate === todayStr;
  const minTime = isToday ? `${nowHours}:${nowMins}` : '00:00';

  const selectedIsoString = useMemo(() => {
    if (!sendDate || !sendTime) return '';
    return wallClockToUtcISO(sendDate, sendTime, timezone);
  }, [sendDate, sendTime, timezone]);

  const selectedDatetime = useMemo(() => {
    if (!selectedIsoString) return null;
    return new Date(selectedIsoString);
  }, [selectedIsoString]);

  const isValid = selectedDatetime && selectedDatetime.getTime() > Date.now();

  const handleSchedule = async () => {
    if (submittingRef.current) return;
    if (!selectedDatetime || !selectedIsoString || !isValid) {
      setError('Please select a future date and time.');
      return;
    }
    submittingRef.current = true;
    setSaving(true);
    setError('');
    setWarning('');
    try {
      const token = await getAuth().currentUser?.getIdToken();
      const body = {
        ...emailData,
        sendAt: selectedIsoString,
        timezone,
      };

      if (existingScheduleId) {
        // Reschedule
        const res = await fetch(`/api/email/scheduled/${existingScheduleId}/reschedule`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ sendAt: selectedIsoString, timezone }),
        });
        if (!res.ok) {
          const d = await res.json();
          throw new Error(d.error || 'Failed to reschedule');
        }
      } else if (broadcast) {
        // Batch campaign schedule — one idempotency key per compose session.
        const result = await scheduleBroadcast({
          recipients: broadcast.recipients,
          subject: emailData.subject,
          htmlBody: emailData.htmlBody,
          sendAt: selectedIsoString,
          timezone,
          idempotencyKey: broadcast.idempotencyKey,
          batchId: broadcast.batchId,
          sentBy: broadcast.sentBy,
          senderAddress: broadcast.senderAddress,
          attachments: broadcast.attachments,
          documentTemplateIds: broadcast.documentTemplateIds,
        });
        // ES-6: partial persistence is a warning, not a success —
        // the recipients whose rows failed were NOT scheduled.
        if (result.partial) {
          setWarning(
            `Scheduled for ${result.persistedCount ?? result.scheduleIds.length} of ${result.recipientCount ?? broadcast.recipients.length} recipients. ` +
            `${result.failedCount ?? 0} could not be saved and were NOT scheduled — please schedule those recipients again.`
          );
          setTimeout(() => { onScheduled?.(); onClose(); }, 3500);
          return;
        }
      } else {
        // New schedule
        const res = await fetch('/api/email/schedule', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const d = await res.json();
          throw new Error(d.error || 'Failed to schedule');
        }
      }

      setSuccess(existingScheduleId ? 'Rescheduled successfully!' : 'Scheduled successfully!');
      setTimeout(() => { onScheduled?.(); onClose(); }, 1200);
    } catch (err: any) {
      setError(err.message || 'Failed to schedule email');
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 flex items-start justify-center p-4 pt-[10vh] bg-black/40 backdrop-blur-sm overflow-y-auto"
      style={{ zIndex }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto animate-fade-in-up relative"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white px-5 py-3.5 border-b border-slate-200 flex items-center justify-between z-10 rounded-t-2xl">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-blue-500" />
            <p className="text-sm font-bold text-slate-800">
              {existingScheduleId ? 'Reschedule Email' : broadcast ? 'Schedule Campaign' : 'Schedule Email'}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg transition-all cursor-pointer" type="button" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Recipient summary */}
          <div className="bg-slate-50 rounded-xl px-4 py-3 space-y-1">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
              {broadcast ? 'Campaign recipients' : 'Recipient'}
            </p>
            {broadcast ? (
              <>
                <p className="text-xs font-semibold text-slate-700">{emailData.toName || 'Batch campaign'}</p>
                <p className="text-[11px] text-slate-500">
                  {broadcast.recipients.length} recipient{broadcast.recipients.length === 1 ? '' : 's'}
                  {broadcast.recipients.length > 0 && (
                    <> — {broadcast.recipients.slice(0, 3).map((r) => r.email).join(', ')}{broadcast.recipients.length > 3 ? '…' : ''}</>
                  )}
                </p>
              </>
            ) : (
              <>
                <p className="text-xs font-semibold text-slate-700">{emailData.toName || emailData.to}</p>
                <p className="text-[11px] text-slate-500">{emailData.to}</p>
              </>
            )}
          </div>

          {/* Subject */}
          {emailData.subject && (
            <div className="bg-slate-50 rounded-xl px-4 py-3 space-y-1">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Subject</p>
              <p className="text-xs font-medium text-slate-700 truncate">{emailData.subject}</p>
            </div>
          )}

          {/* Date & Time */}
          <div className="space-y-3">
            <label className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider">Send Date &amp; Time</label>
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
                <input
                  type="date"
                  value={sendDate}
                  min={todayStr}
                  onChange={e => setSendDate(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 text-xs border border-slate-200 rounded-xl outline-none focus:border-blue-400 focus:ring-2 focus:ring-brand-primary"
                  required
                />
              </div>
              <div className="flex-1 relative">
                <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
                <input
                  type="time"
                  value={sendTime}
                  min={isToday ? minTime : undefined}
                  onChange={e => setSendTime(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 text-xs border border-slate-200 rounded-xl outline-none focus:border-blue-400 focus:ring-2 focus:ring-brand-primary"
                  required
                />
              </div>
            </div>

            {/* Timezone */}
            <div>
              <select
                value={timezone}
                onChange={e => setTimezone(e.target.value)}
                className="w-full px-3 py-2.5 text-xs border border-slate-200 rounded-xl outline-none focus:border-blue-400 focus:ring-2 focus:ring-brand-primary"
              >
                {Intl.supportedValuesOf?.('timeZone')?.map((tz: string) => (
                  <option key={tz} value={tz}>{tz}</option>
                )) || (
                  <>
                    <option value="UTC">UTC</option>
                    <option value="America/New_York">America/New_York</option>
                    <option value="America/Chicago">America/Chicago</option>
                    <option value="America/Denver">America/Denver</option>
                    <option value="America/Los_Angeles">America/Los_Angeles</option>
                    <option value="Europe/London">Europe/London</option>
                    <option value="Europe/Berlin">Europe/Berlin</option>
                    <option value="Asia/Tokyo">Asia/Tokyo</option>
                    <option value="Asia/Shanghai">Asia/Shanghai</option>
                  </>
                )}
              </select>
            </div>

            {selectedDatetime && !isValid && (
              <p className="text-[10px] text-rose-600 font-semibold flex items-center gap-1">
                <AlertCircle className="h-3 w-3" /> Selected time is in the past. Please choose a future time.
              </p>
            )}
            {selectedDatetime && isValid && (
              <p className="text-[10px] text-emerald-600 font-semibold flex items-center gap-1">
                <CheckCircle className="h-3 w-3" /> Will send on {selectedDatetime.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
              </p>
            )}
          </div>

          {/* Preview Toggle */}
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center gap-1.5 text-xs font-bold text-blue-600 hover:text-blue-700 transition cursor-pointer"
            type="button"
          >
            <Eye className="h-3.5 w-3.5" />
            {showPreview ? 'Hide Preview' : 'Preview Email'}
          </button>

          {showPreview && (
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <div className="bg-slate-50 px-4 py-2 border-b border-slate-200">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Email Preview</p>
              </div>
              <div className="p-4 space-y-3 max-h-[300px] overflow-y-auto">
                {emailData.subject && (
                  <div>
                    <p className="text-[11px] font-bold text-slate-400 uppercase">Subject</p>
                    <p className="text-xs font-semibold text-slate-800">{emailData.subject}</p>
                  </div>
                )}
                <div>
                  <p className="text-[11px] font-bold text-slate-400 uppercase">Body</p>
                  <div className="prose prose-xs max-w-none text-xs text-slate-700 mt-1 border border-slate-100 rounded-lg p-3 bg-white"
                    dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(emailData.htmlBody) }}
                  />
                </div>
                {emailData.attachments && emailData.attachments.length > 0 && (
                  <div>
                    <p className="text-[11px] font-bold text-slate-400 uppercase">Attachments ({emailData.attachments.length})</p>
                    <ul className="text-[10px] text-slate-600 space-y-0.5 mt-1">
                      {emailData.attachments.map((a, i) => (
                        <li key={i} className="flex items-center gap-1">
                          <span className="w-1 h-1 rounded-full bg-slate-300" />
                          {a.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {emailData.documentTemplateIds && emailData.documentTemplateIds.length > 0 && (
                  <div>
                    <p className="text-[11px] font-bold text-slate-400 uppercase">Documents to Generate ({emailData.documentTemplateIds.length})</p>
                    <p className="text-[11px] text-slate-500 mt-1">
                      {broadcast
                        ? "Generated from each recipient's own application when the campaign is sent, saved to their Generated Documents, and attached as PDFs. A recipient whose documents fail to generate will not receive the email."
                        : "Generated from the applicant's application when the email is sent, saved to their Generated Documents, and attached as PDFs."}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Error / Success */}
          {error && (
            <div className="flex items-center gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 px-4 py-2.5 rounded-xl">
              <AlertCircle className="h-4 w-4 shrink-0" /><span>{error}</span>
            </div>
          )}
          {success && (
            <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-4 py-2.5 rounded-xl">
              <CheckCircle className="h-4 w-4 shrink-0" /><span>{success}</span>
            </div>
          )}
          {warning && (
            <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-4 py-2.5 rounded-xl">
              <AlertCircle className="h-4 w-4 shrink-0" /><span>{warning}</span>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button onClick={onClose}
              className="px-4 py-2 border border-slate-200 text-xs font-bold rounded-xl text-slate-500 hover:bg-slate-50 transition-all cursor-pointer"
              type="button">Cancel</button>
            <button onClick={handleSchedule} disabled={!sendDate || !sendTime || !isValid || saving}
              className="flex items-center gap-1.5 px-5 py-2 btn-primary disabled:bg-slate-300 text-white text-xs font-bold rounded-xl transition-all cursor-pointer disabled:cursor-not-allowed shadow-sm"
              type="button">
              {saving ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              {existingScheduleId ? 'Reschedule' : saving ? 'Scheduling...' : 'Schedule'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
